import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { formatSummaryValue } from '../utils/summary_utils'
import { pickTranslatedName } from '../utils/events_utils'
import { themedTextClass } from '../utils/css_utils'
import { componentText } from '../utils/i18n_utils'

const t = (key, fallback) => componentText(`summaries.${key}`, fallback)

// A League's summary (round_scoped payload, RTM-10251): one row per entity
// with its total for the round, the legacy "Total Points" table. There are no
// per-tournament columns; instead the name is a button that opens a nested
// table under the row listing the tournaments the entity earned in and the
// value won in each - the legacy points_tournaments2_summary popup, inline.
const LeagueSummaryTable = ({
  metric,
  isTeam,
  tournaments = [],
  rows = [],
  currencySymbol,
  purseDecimalPlaces,
  isManagerSite = false,
}) => {
  const [ expandedIds, setExpandedIds ] = useState(() => new Set())

  // A refreshed payload is a new rows array: start collapsed again rather than
  // keep ids that may no longer be listed.
  useEffect(() => { setExpandedIds(new Set()) }, [ rows ])

  const toggleRow = (id) => {
    setExpandedIds((current) => {
      const next = new Set(current)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const formatValue = (value) => formatSummaryValue(metric, value, currencySymbol, purseDecimalPlaces)

  const entityLabel = isTeam ? t('team', 'Team') : t('player', 'Player')
  const valueLabel = metric === 'purse' ? t('purse', 'Purse') : t('points', 'Points')

  // Tournaments in play order, only those the entity has a value in (the API
  // lists an entity's awards, so an absent id means nothing won there).
  const tournamentsFor = (row) =>
    tournaments.filter((tournament) => row.valuesByTournament[tournament.id] !== undefined)

  return (
    <div className="lbv2__summary-scope">
      <table className="lbv2__summary-table lbv2__summary-table--league">
        <thead>
          <tr>
            <th scope="col" className="lbv2__summary-name">{entityLabel}</th>
            <th scope="col" className="lbv2__summary-total">{t('total', 'Total')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isExpanded = expandedIds.has(row.id)
            const detailsId = `summary-${metric}-${isTeam ? 'team' : 'player'}-${row.id}-tournaments`

            return (
              <React.Fragment key={row.id}>
                <tr>
                  <th scope="row" className="lbv2__summary-name">
                    <button
                      type="button"
                      className={`lbv2__summary-name-btn ${themedTextClass(isManagerSite)}`}
                      aria-expanded={isExpanded}
                      aria-controls={detailsId}
                      onClick={() => toggleRow(row.id)}
                    >
                      {row.name}
                    </button>
                  </th>
                  <td className="lbv2__summary-total">{formatValue(row.roundValue)}</td>
                </tr>
                {isExpanded && (
                  <tr className="lbv2__summary-details-row">
                    <td colSpan={2}>
                      <table id={detailsId} className="lbv2__summary-details-table">
                        <thead>
                          <tr>
                            <th scope="col" className="lbv2__summary-details-name">{t('tournament', 'Tournament')}</th>
                            <th scope="col" className="lbv2__summary-details-value">{valueLabel}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tournamentsFor(row).map((tournament) => (
                            <tr key={tournament.id}>
                              <td className="lbv2__summary-details-name">
                                {pickTranslatedName(tournament.name_translations, tournament.name)}
                              </td>
                              <td className="lbv2__summary-details-value">
                                {formatValue(row.valuesByTournament[tournament.id])}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

LeagueSummaryTable.propTypes = {
  metric: PropTypes.oneOf([ 'points', 'purse' ]).isRequired,
  isTeam: PropTypes.bool,
  tournaments: PropTypes.array,
  rows: PropTypes.array,
  currencySymbol: PropTypes.string,
  purseDecimalPlaces: PropTypes.number,
  isManagerSite: PropTypes.bool,
}

export default LeagueSummaryTable
